import React, { useState, useEffect, useCallback } from 'react';
import axios, { fehlerText } from '../api/http';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Button,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportProjectToPDF } from '../utils/pdfExport';
import { useFeedback } from '../context/FeedbackContext';

// Chart.js Komponenten registrieren
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Interface für die Projektberichtsdaten
interface TimeEntry {
  id: number;
  date: string;
  hours: number;
  userId: number;
  userName: string;
  isDayRateDay: boolean;
  created_at: string;
  description: string;
  endTime: string;
  projectId: number;
  projectName: string;
  startTime: string;
  breakDuration: number;
}

interface UserHours {
  userId: number;
  userName: string;
  hours: number;
}

interface ReportData {
  id: number;
  name: string;
  client: string;
  totalHours: number;
  hoursToBill: number;
  dayRates: number;
  userHours: UserHours[];
  time_entries?: TimeEntry[] | TimeEntry;
}

interface ProjectReport {
  id: number;
  name: string;
  client: string;
  totalHours: number;
  hoursToBill: number;
  dayRates: number;
  userHours: UserHours[];
  time_entries: TimeEntry[];
}

// Hinweis: Basis-URL, Timeout und Authorization-Header werden zentral in
// src/api/http.ts gesetzt. Diese Seite hatte zusätzlich einen eigenen
// Request-Interceptor, der den Token ein zweites Mal angehängt hat.

// Neue Komponente für die Zeiteinträge
const TimeEntriesTable: React.FC<{ entries: ProjectReport['time_entries'] }> = ({ entries }) => {
  if (!entries || entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Keine Zeiteinträge vorhanden
      </Typography>
    );
  }

  // Sortiere die Einträge nach Datum
  const sortedEntries = [...entries].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Datum</TableCell>
          <TableCell>Mitarbeiter</TableCell>
          <TableCell>Startzeit</TableCell>
          <TableCell>Endzeit</TableCell>
          <TableCell>Pause (Min)</TableCell>
          <TableCell align="right">Stunden</TableCell>
          <TableCell>Typ</TableCell>
          <TableCell>Beschreibung</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedEntries.map((entry) => (
          <TableRow key={entry.id}>
            <TableCell>{new Date(entry.date).toLocaleDateString('de-DE')}</TableCell>
            <TableCell>{entry.userName}</TableCell>
            <TableCell>{entry.startTime || '-'}</TableCell>
            <TableCell>{entry.endTime || '-'}</TableCell>
            <TableCell>{entry.breakDuration || '-'}</TableCell>
            <TableCell align="right">{entry.hours.toFixed(1)}</TableCell>
            <TableCell>{entry.isDayRateDay ? 'Tagessatz' : 'Stunden'}</TableCell>
            <TableCell>{entry.description || '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const Reports: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { zeigeErfolg, zeigeFehler } = useFeedback();
  
  // State für Filter
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [reports, setReports] = useState<ProjectReport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [users, setUsers] = useState<Array<{ id: number; name: string }>>([]);
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  
  // Hilfsfunktion, um den aktuellen Monat im Format YYYY-MM zu erhalten
  function getCurrentMonth(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }
  
  // Hilfsfunktion, um alle verfügbaren Monate der letzten 2 Jahre zu generieren
  function getAvailableMonths(): Array<{ value: string, label: string }> {
    const months = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Generiere Monate für die letzten 2 Jahre
    for (let year = currentYear - 1; year <= currentYear; year++) {
      const startMonth = year === currentYear - 1 ? 0 : 0;
      const endMonth = year === currentYear ? currentMonth : 11;
      
      for (let month = startMonth; month <= endMonth; month++) {
        const monthValue = `${year}-${String(month + 1).padStart(2, '0')}`;
        const monthLabel = new Date(year, month, 1).toLocaleString('de-DE', { 
          month: 'long', 
          year: 'numeric' 
        });
        
        months.push({ value: monthValue, label: monthLabel });
      }
    }
    
    // Sortiere absteigend (neueste zuerst)
    return months.reverse();
  }
  
  // Daten für das Balkendiagramm vorbereiten
  const prepareChartData = () => {
    return {
      labels: reports.map(report => report.name),
      datasets: [
        {
          label: 'Gesamtstunden',
          data: reports.map(report => report.totalHours),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };
  };
  
  // Diagramm-Optionen
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `Projektstunden für ${selectedMonth}`,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Stunden',
        },
      },
    },
  };

  // Lade Projekte und Benutzer beim ersten Laden
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [projectsResponse, usersResponse] = await Promise.all([
          axios.get('/api/projects'),
          axios.get('/api/users')
        ]);

        setProjects(projectsResponse.data.projects ?? []);
        setUsers(usersResponse.data.users ?? []);
      } catch (error) {
        setError(fehlerText(error, 'Die Filterdaten konnten nicht geladen werden'));
      }
    };

    loadFilterData();
  }, []);

  // Berichte abrufen
  //
  // Die Zeiteinträge kommen vollständig aus dem Bericht-Endpunkt. Vorher
  // wurden zusätzlich sämtliche Zeiteinträge geladen, nur um Start-/Endzeit
  // und Beschreibung pro Eintrag nachzuschlagen.
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = selectedProject
        ? `/api/reports/project/${selectedProject}/month/${selectedMonth}`
        : `/api/reports/monthly/${selectedMonth}`;

      const response = await axios.get(apiUrl);

      if (!response.data) {
        throw new Error('Keine Daten vom Server erhalten');
      }

      const reportData: ReportData[] = Array.isArray(response.data)
        ? response.data
        : [response.data];

      const processedData = reportData
        .map((report: ReportData): ProjectReport | undefined => {
          if (!report) return undefined;

          let timeEntries = Array.isArray(report.time_entries)
            ? report.time_entries
            : report.time_entries
            ? [report.time_entries]
            : [];

          if (selectedUser) {
            timeEntries = timeEntries.filter(
              (entry: TimeEntry) => Number(entry.userId) === Number(selectedUser)
            );
          }

          if (timeEntries.length === 0) return undefined;

          let hoursToBill = 0;
          let dayRates = 0;

          const processedEntries = timeEntries.map((entry: TimeEntry): TimeEntry => {
            const isDayRateDay = entry.hours >= 5 || entry.isDayRateDay === true;

            if (isDayRateDay) {
              dayRates++;
            } else {
              hoursToBill += entry.hours;
            }

            return {
              id: entry.id,
              date: entry.date,
              hours: entry.hours,
              userId: entry.userId,
              userName: entry.userName,
              isDayRateDay,
              created_at: entry.created_at || '',
              description: entry.description || '',
              startTime: entry.startTime || '',
              endTime: entry.endTime || '',
              projectId: entry.projectId || report.id,
              projectName: entry.projectName || report.name,
              breakDuration: entry.breakDuration || 0
            };
          });

          return {
            id: report.id,
            name: report.name,
            client: report.client || '',
            totalHours: processedEntries.reduce(
              (sum: number, entry: TimeEntry) => sum + entry.hours,
              0
            ),
            hoursToBill,
            dayRates,
            userHours: report.userHours || [],
            time_entries: processedEntries
          };
        })
        .filter((report): report is ProjectReport => report !== undefined);

      if (processedData.length === 0) {
        setError('Keine Daten für die ausgewählten Filter gefunden');
      }

      setReports(processedData);
    } catch (error) {
      setError(fehlerText(error, 'Fehler beim Laden der Berichte'));
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedProject, selectedUser]);

  // Lade Berichte, wenn sich die Filter ändern
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const toggleProjectExpansion = (projectId: number) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}>
          <Typography variant="h4">
            Berichte
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => {
              if (reports.length === 0) {
                zeigeFehler('Keine Daten zum Exportieren vorhanden.');
                return;
              }
              reports.forEach(project => exportProjectToPDF(project, selectedMonth));
              zeigeErfolg(
                reports.length === 1
                  ? 'PDF wurde erstellt'
                  : `${reports.length} PDF-Dateien wurden erstellt`
              );
            }}
            disabled={reports.length === 0}
          >
            PDF Export
          </Button>
        </Box>
        
        {/* Filter */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Monat</InputLabel>
              <Select
                value={selectedMonth}
                label="Monat"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {getAvailableMonths().map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Mitarbeiter</InputLabel>
              <Select
                value={selectedUser}
                label="Mitarbeiter"
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                <MenuItem value="">Alle Mitarbeiter</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/*
            Der Projekt-Filter war ausgeblendet, weil der Detail-Endpunkt die
            Zeiteinträge in einem anderen Format lieferte und der Bericht
            dadurch leer blieb. Beide Endpunkte antworten jetzt gleich.
          */}
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Projekt</InputLabel>
              <Select
                value={selectedProject}
                label="Projekt"
                onChange={(e) => setSelectedProject(e.target.value)}
              >
                <MenuItem value="">Alle Projekte</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id.toString()}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : reports.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            Keine Daten für den ausgewählten Monat verfügbar.
          </Alert>
        ) : (
          <>
            {/* Diagramm */}
            <Paper 
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                mb: 3,
                height: { xs: 300, sm: 400 }
              }}
            >
              <Bar data={prepareChartData()} options={chartOptions} />
            </Paper>

            {/* Zusammenfassung */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Gesamtstunden
                    </Typography>
                    <Typography variant="h4">
                      {reports.reduce((sum, project) => sum + project.totalHours, 0).toFixed(1)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Abzurechnende Stunden
                    </Typography>
                    <Typography variant="h4">
                      {reports.reduce((sum, project) => sum + project.hoursToBill, 0).toFixed(1)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Tagessätze
                    </Typography>
                    <Typography variant="h4">
                      {reports.reduce((sum, project) => sum + project.dayRates, 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Detaillierte Daten */}
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {reports.map((project) => (
                  <Card key={project.id}>
                    <CardContent>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 2
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          cursor: 'pointer',
                          flex: 1
                        }} onClick={() => toggleProjectExpansion(project.id)}>
                          <Typography variant="h6">
                            {project.name}
                          </Typography>
                          {expandedProjects.has(project.id) ? 
                            <KeyboardArrowUpIcon /> : 
                            <KeyboardArrowDownIcon />
                          }
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            exportProjectToPDF(project, selectedMonth);
                          }}
                          color="primary"
                          sx={{ ml: 1 }}
                        >
                          <FileDownloadIcon />
                        </IconButton>
                      </Box>
                      {project.client && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {project.client}
                        </Typography>
                      )}
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Gesamtstunden
                            </Typography>
                            <Typography variant="body1">
                              {project.totalHours.toFixed(1)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Abzurechnen
                            </Typography>
                            <Typography variant="body1">
                              {project.hoursToBill.toFixed(1)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Tagessätze
                            </Typography>
                            <Typography variant="body1">
                              {project.dayRates}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                      <Collapse in={expandedProjects.has(project.id)}>
                        <Box sx={{ mt: 2 }}>
                          <TimeEntriesTable entries={project.time_entries} />
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Projekt</TableCell>
                      <TableCell>Kunde</TableCell>
                      <TableCell align="right">Gesamtstunden</TableCell>
                      <TableCell align="right">Abzurechnende Stunden</TableCell>
                      <TableCell align="right">Tagessätze</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Export</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((project) => (
                      <React.Fragment key={project.id}>
                        <TableRow>
                          <TableCell>{project.name}</TableCell>
                          <TableCell>{project.client || '-'}</TableCell>
                          <TableCell align="right">{project.totalHours.toFixed(1)}</TableCell>
                          <TableCell align="right">{project.hoursToBill.toFixed(1)}</TableCell>
                          <TableCell align="right">{project.dayRates}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => toggleProjectExpansion(project.id)}
                            >
                              {expandedProjects.has(project.id) ? 
                                <KeyboardArrowUpIcon /> : 
                                <KeyboardArrowDownIcon />
                              }
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                exportProjectToPDF(project, selectedMonth);
                              }}
                              color="primary"
                            >
                              <FileDownloadIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                            <Collapse in={expandedProjects.has(project.id)} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 1 }}>
                                <TimeEntriesTable entries={project.time_entries} />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default Reports; 